import type { OutcomeDefinition } from "@repo/app-types/entities/outcome";
import type { OutcomeSampleGradingEntry } from "@repo/app-types/entities/playbookProposalDetail";
import { getGradingDisplayNames } from "@repo/app-types/entities/teammatePerformanceSummary";
import {
  SlideoverDialog,
  SlideoverDialogContent,
  SlideoverDialogTitleHeader,
} from "@repo/ui/Slideover/slideoverDialog";
import type { FC } from "react";
import { usePlaybookGradingQuery } from "../../api/entities/performancePlaybook.js";
import { appPaths } from "../../app/appPaths.js";
import { MatchAnalysisSlideoverScaffold } from "../../features/transcriptViewer/matchAnalysisSlideover.js";
import { renderOutcomeSampleGrade } from "./outcomeSampleGradingsTable.js";

interface OutcomeGradingDialogContainerProps {
  entry: OutcomeSampleGradingEntry;
  outcomeDefinition: OutcomeDefinition;
}

export const OutcomeGradingDialogContainer: FC<
  OutcomeGradingDialogContainerProps
> = (props) => {
  const { entry, outcomeDefinition } = props;
  const {
    currentData: grading,
    isFetching,
    isError,
  } = usePlaybookGradingQuery({
    gradingId: entry.grading.id,
  });

  if (!grading) {
    return (
      <SlideoverDialog label={outcomeDefinition.name}>
        <SlideoverDialogTitleHeader isLoading={isFetching}>
          {outcomeDefinition.name}
        </SlideoverDialogTitleHeader>
        <SlideoverDialogContent>
          {!isFetching ? (
            <p className="p-4 text-sm text-muted-foreground">
              {isError
                ? "The call could not be loaded. Please try again."
                : "Call not found."}
            </p>
          ) : null}
        </SlideoverDialogContent>
      </SlideoverDialog>
    );
  }

  return (
    <MatchAnalysisSlideoverScaffold
      title={outcomeDefinition.name}
      subtitle={{
        contactName: getGradingDisplayNames(grading).primaryDisplayName ?? null,
        contactLinkUrl: appPaths.playbookCall.build({
          playbookId: grading.playbook_id,
          gradingId: grading.id,
        }),
        recordedAt: grading.last_recorded_at,
      }}
      metadataEntries={[
        {
          label: "Current grade",
          value: renderOutcomeSampleGrade(
            outcomeDefinition,
            entry.baseline_grade,
          ),
        },
        {
          label: "Test grade",
          value: renderOutcomeSampleGrade(
            outcomeDefinition,
            entry.tested_grade,
          ),
        },
      ]}
      analysis={
        entry.tested_grade?.commentary
          ? [
              {
                type: "text",
                fragments: [
                  { type: "plain", text: entry.tested_grade.commentary },
                ],
              },
            ]
          : []
      }
      gradingId={grading.id}
      recordings={grading.recordings}
    />
  );
};
